import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Award, Compass, Heart, Leaf, MapPin, Mountain, ShieldCheck, Sparkles, Users } from 'lucide-react';
import { brand, stats, whatsappLink } from '../mock';

const milestones = [
  { year: '2013', title: 'Founded in Varanasi', desc: 'Started as a small Kashi darshan operator serving pilgrims from all over India.' },
  { year: '2016', title: 'Char Dham Specialist', desc: 'Became one of the trusted operators for Uttarakhand Char Dham & Do Dham yatras.' },
  { year: '2019', title: 'Fleet Expansion', desc: 'Added Innova, Tempo Travellers, Urbania & luxury coaches to our own fleet.' },
  { year: '2022', title: 'Pan-India Circuits', desc: 'Expanded operations to Kashmir, Ladakh, Kerala, Goa, Rajasthan & North-East.' },
  { year: '2025', title: '2,000+ Happy Guests', desc: 'Trusted by pilgrims, families, corporate groups & foreign visitors.' },
];

const values = [
  { icon: Heart, title: 'Guest First', desc: 'Every guest is treated as family. Your comfort & safety is our first priority.' },
  { icon: ShieldCheck, title: 'Transparency', desc: 'No hidden charges. Clear inclusions/exclusions on every quote.' },
  { icon: Sparkles, title: 'Attention to Detail', desc: 'From reserved aarti seats to VIP darshan — we handle every small thing.' },
  { icon: Leaf, title: 'Responsible Travel', desc: 'We support local guides, homestays & eco-friendly practices.' },
];

const team = [
  { name: 'Punit Rai', role: 'Founder & CEO', img: 'https://customer-assets-jai6qajn.emergentagent.net/job_bharat-journey-5/artifacts/57s1n418_2fb1e5a0-080a-4ff5-a509-488984164a8c.jpeg' },
];

export default function About() {
  return (
    <div className="bg-white">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1571536802807-30451e3955d8" alt="Varanasi ghats" className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(6,18,40,0.85), rgba(6,18,40,0.9))' }} />
        </div>
        <div className="relative max-w-6xl mx-auto px-5 lg:px-8 py-24 md:py-32 text-center text-white">
          <span className="eyebrow text-orange-300 border-orange-300/40 bg-orange-300/10">About Us</span>
          <h1 className="mt-5 font-serif text-4xl md:text-6xl font-bold leading-tight">Crafting Journeys from the <span className="accent-word">Land of Kashi</span></h1>
          <p className="mt-5 max-w-3xl mx-auto text-blue-50/85 text-lg leading-relaxed">
            Goodwishes India Tours is a Varanasi-based, family-owned travel company dedicated to designing spiritual, cultural & leisure experiences across the length & breadth of Incredible India.
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="grid grid-cols-2 gap-4">
            <img src="https://images.unsplash.com/photo-1571536802807-30451e3955d8" alt="" className="rounded-2xl h-64 w-full object-cover" />
            <img src="https://images.unsplash.com/photo-1652059468417-3b44ff25afdf" alt="" className="rounded-2xl h-64 w-full object-cover mt-6" />
            <img src="https://images.unsplash.com/photo-1712733900711-d0b929d0d7cc" alt="" className="rounded-2xl h-64 w-full object-cover -mt-6" />
            <img src="https://images.unsplash.com/photo-1707381076957-ec19b90e9cbe" alt="" className="rounded-2xl h-64 w-full object-cover" />
          </div>
          <div>
            <span className="eyebrow">Our Story</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-navy mt-4">Born by the Ganga, powered by <span className="accent-word">devotion & service</span></h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              What began in 2013 as a two-vehicle Kashi darshan service is today one of Varanasi’s most trusted tour operators serving over 2,000 travellers every year. Rooted in the timeless traditions of Kashi, we combine deep local knowledge with modern comforts — clean AC vehicles, verified hotels, experienced guides and 24x7 concierge support.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Whether you dream of hearing the Ganga aarti bells, trekking to Kedarnath, drifting through Kerala’s backwaters or celebrating a full-moon night at the White Rann — we design the journey around you, not the other way around.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/packages" className="btn-orange">See Our Packages</Link>
              <a href={whatsappLink('Hello Goodwishes team, I want to know more about you.')} target="_blank" rel="noreferrer" className="btn-outline-navy">Talk to Us</a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-navy py-14" style={{ backgroundColor: '#061228' }}>
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s) => (
            <div key={s.label} className="text-center text-white">
              <div className="text-3xl md:text-4xl font-bold text-orange-400">{s.value}</div>
              <div className="mt-1 text-sm text-blue-100/80">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl bg-orange-50 p-8 border border-orange-100">
            <div className="w-12 h-12 rounded-full bg-orange-500 text-white flex items-center justify-center"><Compass className="w-6 h-6" /></div>
            <h3 className="font-serif text-2xl font-bold text-navy mt-4">Our Mission</h3>
            <p className="text-slate-700 mt-3 leading-relaxed">To make every spiritual & leisure journey across India stress-free, affordable and deeply memorable — through honest pricing, personal attention and rock-solid on-ground execution.</p>
          </div>
          <div className="rounded-2xl bg-slate-50 p-8 border border-slate-100">
            <div className="w-12 h-12 rounded-full bg-navy text-white flex items-center justify-center" style={{ backgroundColor: '#0b1e3d' }}><Mountain className="w-6 h-6" /></div>
            <h3 className="font-serif text-2xl font-bold text-navy mt-4">Our Vision</h3>
            <p className="text-slate-700 mt-3 leading-relaxed">To be India’s most-loved boutique tour company — the first name that comes to mind when a family plans their next darshan or holiday, both in India and from abroad.</p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="eyebrow">Our Values</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-navy mt-4">What We Stand For</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-6 mt-10">
            {values.map((v) => (
              <div key={v.title} className="rounded-2xl bg-white p-6 border border-slate-100 card-lift">
                <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center"><v.icon className="w-6 h-6 text-orange-500" /></div>
                <h4 className="mt-4 font-serif text-lg font-bold text-navy">{v.title}</h4>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="section">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="eyebrow">Our Journey</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-navy mt-4">Milestones</h2>
          </div>
          <div className="mt-10 border-l-2 border-orange-200 pl-6 space-y-6">
            {milestones.map((m) => (
              <div key={m.year} className="relative">
                <div className="absolute -left-[34px] w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center"><Award className="w-3.5 h-3.5 text-white" /></div>
                <div className="rounded-xl bg-white border border-slate-100 p-5 shadow-sm">
                  <div className="text-xs text-orange-500 font-bold">{m.year}</div>
                  <h4 className="mt-1 font-serif text-xl font-bold text-navy">{m.title}</h4>
                  <p className="text-slate-600 text-sm mt-1">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section bg-slate-50">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="eyebrow">Meet the Founder</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-navy mt-4">The visionary behind your journey</h2>
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-8 items-center rounded-2xl bg-white border border-slate-100 p-6 md:p-10 shadow-sm">
            <div className="h-96 md:h-[28rem] rounded-xl overflow-hidden img-zoom bg-slate-100">
              <img src={team[0].img} alt={team[0].name} className="w-full h-full object-cover object-top" />
            </div>
            <div>
              <span className="eyebrow">{team[0].role}</span>
              <h3 className="mt-3 font-serif text-3xl md:text-4xl font-bold text-navy">{team[0].name}</h3>
              <p className="mt-4 text-slate-600 leading-relaxed">
                A proud son of Kashi, Punit Rai founded Goodwishes India Tours in 2013 with a simple dream — to make every pilgrim and family from Varanasi travel across India with the same warmth, honesty and comfort he would want for his own family.
              </p>
              <p className="mt-3 text-slate-600 leading-relaxed">
                Over a decade later, that dream powers 2,000+ happy yatras every year across 45+ destinations in India & Nepal. Punit personally oversees every itinerary, hand-picks partner hotels and keeps his phone open 24×7 for guests on the road.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-orange-50 text-orange-700 px-3 py-1.5 rounded-full">12+ Years in Travel</span>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-full">Char Dham Specialist</span>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-sky-50 text-sky-700 px-3 py-1.5 rounded-full">Based in Varanasi</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="rounded-3xl bg-navy text-white p-10 md:p-14 text-center shadow-xl" style={{ backgroundColor: '#0b1e3d' }}>
            <Users className="w-10 h-10 text-orange-400 mx-auto" />
            <h2 className="font-serif text-3xl md:text-4xl font-bold mt-4">Ready to plan your next journey?</h2>
            <p className="mt-3 text-blue-100/80 max-w-2xl mx-auto">Whether it’s a 2-day Ayodhya darshan or an 11-day Char Dham yatra, our team is a WhatsApp away.</p>
            <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
              <a href={whatsappLink('Hello team, I want to plan a trip.')} target="_blank" rel="noreferrer" className="btn-orange">Chat on WhatsApp</a>
              <Link to="/packages" className="inline-flex items-center gap-2 bg-white/10 border border-white/25 hover:bg-white/20 px-5 py-2.5 rounded-full font-semibold transition-colors">Browse Packages</Link>
            </div>
            <div className="mt-6 text-sm text-blue-100/60 flex items-center justify-center gap-2"><MapPin className="w-4 h-4 text-orange-400" />{brand.address}</div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
